import { FiSearch, FiAward, FiClipboard, FiBookOpen, FiShield, FiSettings, FiBarChart2, FiTag } from 'react-icons/fi';

export const services = [
  {
    id: 'iso-certification',
    icon: <FiSettings />,
    title: 'ISO Management Systems',
    tagline: 'Achieve global recognition for your food safety standards',
    color: '#1a3a5c',
    heroImg: '/services/ISOTraining.png',
    summary: 'Expert end-to-end consulting to help your business achieve ISO 22000, FSSC 22000, BRC, SQF, and other globally recognised food safety certifications.',
    description: `ISO and GFSI-recognised certifications signal to customers, retailers, and regulators that your food safety management system meets the highest international standards. However, the certification journey can be complex and time-consuming without expert guidance.

Smart Quality Systems walks with you through every stage — from gap assessment and system design to documentation, staff training, internal audits, and certification body liaison — to ensure your first-time certification success.`,
    benefits: [
      'Open doors to international markets and major retailers',
      'Reduce food safety incidents and associated costs',
      'Demonstrate due diligence to regulators and insurers',
      'Improve operational efficiency through documented systems',
      'Increase customer and consumer confidence',
      'Gain competitive advantage over non-certified competitors',
    ],
    process: [
      { step: '01', title: 'Gap Assessment', desc: 'We compare your current systems against certification requirements and identify what needs to be developed.' },
      { step: '02', title: 'System Development', desc: 'We help you build or enhance your FSMS documentation, procedures, and records to meet the standard.' },
      { step: '03', title: 'Training & Awareness', desc: 'We train your team on the requirements of the standard and their roles within the FSMS.' },
      { step: '04', title: 'Internal Audit', desc: 'We conduct a thorough internal audit to verify readiness before the certification body visits.' },
      { step: '05', title: 'Certification Audit Support', desc: 'We support you during the certification body\'s audit and help address any non-conformances raised.' },
    ],
    faqs: [
      { q: 'Which certifications do you support?', a: 'ISO 22000, FSSC 22000, BRC Global Standard, SQF, IFS, and FDA compliance for export markets.' },
      { q: 'How long does certification take?', a: 'Typically 3–9 months from gap assessment to certificate, depending on your starting point.' },
      { q: 'Will you help us maintain certification?', a: 'Yes — we offer ongoing support for annual surveillance audits and recertification.' },
    ],
  },
  {
    id: 'regulatory-compliance',
    icon: <FiClipboard />,
    title: 'Regulatory Compliance',
    tagline: 'Navigate complex food regulations with confidence',
    color: '#0d3b6e',
    heroImg: '/services/regulatory.png',
    summary: 'Comprehensive regulatory compliance services to ensure your products, labelling, and operations meet FDA Ghana, GSA, and international food law requirements.',
    description: `The regulatory landscape for food businesses in Ghana and the export market is constantly evolving. Non-compliance can result in product seizures, fines, reputational damage, and loss of market access.

Our regulatory compliance team provides up-to-date guidance on all aspects of food law — from product registration and labelling requirements to import/export regulations and novel food approvals — keeping your business fully compliant at all times.`,
    benefits: [
      'Avoid costly regulatory penalties and product seizures',
      'Successfully register products with FDA Ghana and GSA',
      'Ensure labelling compliance for local and export markets',
      'Navigate import/export regulatory requirements',
      'Receive advance notice of regulatory changes that affect your business',
      'Reduce legal risk through proactive compliance management',
    ],
    process: [
      { step: '01', title: 'Compliance Assessment', desc: 'We assess your current products, labelling, and operations against applicable regulations.' },
      { step: '02', title: 'Gap Identification', desc: 'We identify and prioritise all areas of non-compliance and regulatory risk.' },
      { step: '03', title: 'Remediation Plan', desc: 'We develop a practical action plan to bring your business into full compliance.' },
      { step: '04', title: 'Implementation Support', desc: 'We support your team through label revisions, reformulations, and process changes.' },
      { step: '05', title: 'Regulatory Liaison', desc: 'We communicate with regulatory authorities on your behalf where required.' },
    ],
    faqs: [
      { q: 'Do you handle FDA Ghana product registration?', a: 'Yes — we manage the entire product registration process with FDA Ghana including application, liaison, and follow-up.' },
      { q: 'Can you help with export market compliance?', a: 'Yes — we advise on EU, UK, US, and other international market requirements.' },
      { q: 'How do you stay current on regulatory changes?', a: 'We maintain active memberships with regulatory bodies and professional associations to monitor all changes.' },
    ],
  },
  {
    id: 'pest-control',
    icon: <FiShield />,
    title: 'Pest Control & Fumigation',
    tagline: 'Protect your facility and products from pest contamination',
    color: '#1a3a5c',
    heroImg: '/services/PestControl.png',
    summary: 'Integrated pest management advisory services tailored specifically for food manufacturing, processing, and storage environments.',
    description: `Pest infestations in food facilities can cause catastrophic product losses, regulatory shutdowns, and irreparable brand damage. Effective pest management in food environments requires a specialised approach that balances pest control effectiveness with food safety requirements.

Our IPM advisors work with your facility to design, implement, and maintain a comprehensive pest management programme that meets the stringent requirements of food safety standards including FSSC 22000, BRC, and FDA Ghana.`,
    benefits: [
      'Prevent pest-related product contamination and recalls',
      'Meet pest control requirements for food safety certification',
      'Reduce reliance on chemical pesticides in food areas',
      'Documented pest management records for audit compliance',
      'Proactive monitoring to detect pest activity early',
      'Staff training on pest awareness and reporting',
    ],
    process: [
      { step: '01', title: 'Facility Risk Assessment', desc: 'We conduct a thorough inspection to identify pest entry points, harbourage areas, and risk zones.' },
      { step: '02', title: 'IPM Programme Design', desc: 'We develop a customised integrated pest management programme for your facility.' },
      { step: '03', title: 'Implementation Support', desc: 'We support the rollout of your IPM programme and train your designated pest control coordinator.' },
      { step: '04', title: 'Monitoring & Records', desc: 'We help you establish monitoring schedules and documentation systems for audit purposes.' },
      { step: '05', title: 'Periodic Reviews', desc: 'We conduct regular programme reviews and update recommendations based on findings.' },
    ],
    faqs: [
      { q: 'Do you carry out actual pest control treatments?', a: 'We provide advisory and programme management services. We work alongside your licensed pest control contractor to ensure their activities align with food safety requirements.' },
      { q: 'What pests does the programme cover?', a: 'Rodents, insects (cockroaches, flies, beetles, moths), birds, and other pests relevant to your facility type.' },
      { q: 'How often should pest control reviews be conducted?', a: 'Typically quarterly as a minimum, with more frequent monitoring in high-risk areas.' },
    ],
  },
  {
    id: 'haccp-development',
    icon: <FiBarChart2 />,
    title: 'Research & Product Development',
    tagline: 'Science-based hazard analysis for safer food products',
    color: '#0B2545',
    heroImg: '/services/RandD.png',
    summary: 'Comprehensive HACCP plan development and review services based on Codex Alimentarius principles, ensuring effective control of food safety hazards.',
    description: `A scientifically sound HACCP plan is the cornerstone of any effective food safety management system. Yet many businesses operate with outdated, incomplete, or poorly validated HACCP plans that fail to adequately control food safety hazards.

Our HACCP specialists develop rigorous, science-based HACCP plans that genuinely reflect your processes, identify all relevant hazards, and establish effective critical control points — providing real protection against food safety incidents rather than just documentation for auditors.`,
    benefits: [
      'Scientifically validated hazard analysis covering biological, chemical, and physical hazards',
      'Properly established CCPs with validated critical limits',
      'Effective monitoring, corrective action, and verification procedures',
      'HACCP plans that satisfy certification and regulatory requirements',
      'Training for your team on HACCP principles and their application',
      'Ongoing HACCP plan review and update services',
    ],
    process: [
      { step: '01', title: 'Preliminary Steps', desc: 'We assemble the HACCP team, define scope, and describe your products and intended use.' },
      { step: '02', title: 'Process Flow', desc: 'We map your process flow diagram and verify it on-site.' },
      { step: '03', title: 'Hazard Analysis', desc: 'We conduct a thorough hazard analysis identifying all significant biological, chemical, and physical hazards.' },
      { step: '04', title: 'CCP Determination', desc: 'We apply the decision tree to identify Critical Control Points.' },
      { step: '05', title: 'HACCP Plan Completion', desc: 'We establish critical limits, monitoring procedures, corrective actions, and verification activities.' },
    ],
    faqs: [
      { q: 'How often should a HACCP plan be reviewed?', a: 'At least annually and whenever there is a change to products, ingredients, processes, or equipment.' },
      { q: 'Can you develop HACCP plans for multiple product lines?', a: 'Yes — we develop individual HACCP plans for each distinct product or process category.' },
      { q: 'Do you provide HACCP plan validation?', a: 'Yes — validation is an essential part of our HACCP development process.' },
    ],
  },
  {
    id: 'food-labelling',
    icon: <FiTag />,
    title: 'Contract Packaging',
    tagline: 'Get your products to market compliantly and quickly',
    color: '#1a3a5c',
    heroImg: '/services/ContractPack.png',
    summary: 'End-to-end food labelling compliance reviews and product registration services for the Ghanaian market and international export markets.',
    description: `Correct food labelling is both a legal requirement and a powerful consumer communication tool. Getting it wrong can result in regulatory action, product withdrawal, and consumer complaints. Getting it right builds trust and opens markets.

Our labelling specialists review your labels against current FDA Ghana, GSA, and relevant international requirements, identify all non-conformances, and guide you through the corrections needed to achieve full compliance — then support you through the product registration process.`,
    benefits: [
      'Compliant labels that meet FDA Ghana and GSA requirements',
      'Accurate nutritional information and allergen declarations',
      'Successful product registration with regulatory bodies',
      'Label compliance for export to EU, UK, US, and other markets',
      'Reduced risk of regulatory action and product withdrawal',
      'Consumer-friendly label design guidance',
    ],
    process: [
      { step: '01', title: 'Label Review', desc: 'We review all current labels against applicable regulations and identify non-conformances.' },
      { step: '02', title: 'Correction Guidance', desc: 'We provide detailed guidance on all required label corrections with regulatory references.' },
      { step: '03', title: 'Nutritional Analysis', desc: 'We advise on nutritional analysis requirements and help you obtain compliant nutritional data.' },
      { step: '04', title: 'Registration Application', desc: 'We prepare and submit your product registration application to the relevant regulatory body.' },
      { step: '05', title: 'Approval Follow-Up', desc: 'We liaise with regulatory authorities and manage the approval process through to certificate of registration.' },
    ],
    faqs: [
      { q: 'How long does FDA Ghana product registration take?', a: 'Typically 3–6 months from submission of a complete application. We work to ensure your application is complete and accurate to avoid delays.' },
      { q: 'Do you handle imported product registration?', a: 'Yes — we assist importers with product registration for all food categories.' },
      { q: 'What if our label has allergen information errors?', a: 'Allergen labelling errors are a serious compliance and safety issue. We prioritise these and help you correct them quickly.' },
    ],
  },
];

export default services;
